import { useEffect, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { formatDistanceToNow } from 'date-fns'
import { ArrowLeft, EyeOff, Eye, Pencil } from 'lucide-react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Skeleton } from '@/components/ui/skeleton'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { artworkApi } from '@/features/artworks/api/artworkApi'
import { CategoryBadge, ConceptChip, SourceBadges } from '../components/ConceptBadges'
import { ConceptGraph } from '../components/ConceptGraph'
import { ConceptSettings, ConceptStructure } from '../components/ConceptCuration'
import { ConceptProposals, ConceptRelations } from '../components/ConceptRelations'
import { WeightLabel } from '../components/ArtworkTile'
import { useConcept, useConceptArtworks, useConceptGraph, useCurateConcept } from '../hooks/useKnowledge'
import type { Concept, CurationDecision } from '../api/knowledgeApi'

const ARTWORK_PAGE = 60

const DECISION_LABEL: Record<CurationDecision['action'], string> = {
  rename: 'Renamed',
  hide: 'Hidden',
  unhide: 'Unhidden',
  merge: 'Merge recorded',
  separate: 'Split recorded',
  reject: 'Removal recorded',
  note: 'Note',
  category: 'Category set',
  weight: 'Weight set',
  dismiss: 'Dismissed',
  link: 'Relation added',
  distinct: 'Marked different',
}

function decisionDetail(d: CurationDecision, buildName: string): string | null {
  const p = d.payload as { to?: unknown; suggested?: boolean }
  if (d.action === 'rename') return `→ ${String(p.to ?? buildName)}`
  if (d.action === 'category') return `→ ${p.to ?? "build's"}${p.suggested ? ' (accepted suggestion)' : ''}`
  if (d.action === 'weight') return `→ ${p.to == null ? 'automatic' : Number(p.to).toFixed(2)}`
  if (d.revokedAt) return 'revoked'
  if (d.appliedEffect) return d.appliedEffect
  return null
}

function CuratePanel({ concept }: { concept: Concept }) {
  const curate = useCurateConcept(concept.ucid)
  const [editing, setEditing] = useState(false)
  const [name, setName] = useState(concept.displayName ?? '')
  const [notes, setNotes] = useState(concept.notes ?? '')

  useEffect(() => {
    setName(concept.displayName ?? '')
    setNotes(concept.notes ?? '')
  }, [concept.ucid, concept.displayName, concept.notes])

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-sm">Name, notes, visibility</CardTitle>
        <CardDescription>
          Kept across nightly builds and recorded in the history below. Hidden concepts are left out
          of browsing and search.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {editing ? (
          <>
            <div>
              <label className="text-xs text-slate-500">Display name (blank = the build&apos;s name)</label>
              <Input value={name} placeholder={concept.buildName} onChange={(e) => setName(e.target.value)} />
            </div>
            <div>
              <label className="text-xs text-slate-500">Notes</label>
              <Textarea rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} />
            </div>
            <div className="flex gap-2">
              <Button
                size="sm"
                disabled={curate.isPending}
                onClick={() =>
                  curate.mutate({ displayName: name.trim() || null, notes: notes.trim() || null }, { onSuccess: () => setEditing(false) })
                }
              >
                Save
              </Button>
              <Button size="sm" variant="outline" onClick={() => setEditing(false)}>
                Cancel
              </Button>
            </div>
          </>
        ) : (
          <div className="flex flex-wrap gap-2">
            <Button size="sm" variant="outline" onClick={() => setEditing(true)}>
              <Pencil className="mr-1.5 h-3.5 w-3.5" /> Rename / notes
            </Button>
            <Button
              size="sm"
              variant="outline"
              disabled={curate.isPending}
              onClick={() => curate.mutate({ hidden: !concept.hidden })}
            >
              {concept.hidden ? <Eye className="mr-1.5 h-3.5 w-3.5" /> : <EyeOff className="mr-1.5 h-3.5 w-3.5" />}
              {concept.hidden ? 'Unhide' : 'Hide'}
            </Button>
          </div>
        )}
        {concept.notes && !editing && <p className="whitespace-pre-wrap text-sm text-slate-600">{concept.notes}</p>}
      </CardContent>
    </Card>
  )
}

function ArtworksTab({ ucid, total }: { ucid: string; total: number }) {
  const [offset, setOffset] = useState(0)
  const { data, isFetching } = useConceptArtworks(ucid, offset, ARTWORK_PAGE)
  return (
    <div className={isFetching ? 'opacity-70' : undefined}>
      <div className="grid grid-cols-3 gap-3 sm:grid-cols-4 lg:grid-cols-6">
        {data?.data.map((a) => (
          <Link key={a.id} to={`/app/artworks/${a.id}`} className="group">
            <div className="aspect-square overflow-hidden rounded border bg-slate-100">
              <img
                src={artworkApi.getArtworkImageUrl(a.id)}
                alt={a.name}
                loading="lazy"
                className="h-full w-full object-cover transition group-hover:scale-105"
              />
            </div>
            <div className="mt-1 truncate text-xs font-medium">{a.name}</div>
            <div className="truncate text-[11px] text-slate-500">
              {a.artistName ?? '—'}
              {a.status !== 'PUBLISHED' && <span className="ml-1 text-amber-600">· {a.status.toLowerCase()}</span>}
            </div>
          </Link>
        ))}
      </div>
      {total > ARTWORK_PAGE && (
        <div className="mt-4 flex items-center justify-between text-sm text-slate-500">
          <span>
            {offset + 1}–{Math.min(offset + ARTWORK_PAGE, total)} of {total.toLocaleString()}
          </span>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - ARTWORK_PAGE))}>
              Previous
            </Button>
            <Button size="sm" variant="outline" disabled={offset + ARTWORK_PAGE >= total} onClick={() => setOffset(offset + ARTWORK_PAGE)}>
              Next
            </Button>
          </div>
        </div>
      )}
    </div>
  )
}

export function ConceptPage() {
  const { ucid } = useParams<{ ucid: string }>()
  const navigate = useNavigate()
  const { data, isLoading, error } = useConcept(ucid)
  const [showArtworks, setShowArtworks] = useState(true)
  const [breadth, setBreadth] = useState(30)
  const { data: graph, isFetching: graphLoading } = useConceptGraph(data?.concept.ucid, breadth, showArtworks ? 12 : 0)

  // A UCID retired by a merge lands here with the survivor's data: move the URL to the survivor.
  useEffect(() => {
    if (data?.redirectedFrom) navigate(`/app/knowledge/${data.concept.ucid}`, { replace: true })
  }, [data, navigate])

  if (isLoading) {
    return (
      <div className="space-y-4 p-6">
        <Skeleton className="h-8 w-80" />
        <Skeleton className="h-[520px] w-full" />
      </div>
    )
  }
  if (error || !data) {
    return (
      <div className="p-6">
        <Link to="/app/knowledge/concepts" className="text-sm text-slate-500 hover:underline">
          ← Concept catalogue
        </Link>
        <p className="mt-4 text-sm text-slate-600">This concept does not exist in the current graph.</p>
      </div>
    )
  }

  const { concept, neighbors, coOccurring, decisions } = data
  const aliases = concept.memberNames.filter((m) => m !== concept.name)

  return (
    <div className="space-y-6 p-6">
      <div>
        <Link to="/app/knowledge/concepts" className="inline-flex items-center text-sm text-slate-500 hover:underline">
          <ArrowLeft className="mr-1 h-3.5 w-3.5" /> Concept catalogue
        </Link>
        <div className="mt-2 flex flex-wrap items-center gap-3">
          {data.rules?.some((r) => r.action === 'reject' && !r.appliedStatus) && (
            <span className="rounded bg-rose-100 px-2 py-0.5 text-xs text-rose-800">removal pending</span>
          )}
          <h1 className="text-2xl font-semibold">{concept.name}</h1>
          <CategoryBadge category={concept.category} />
          <SourceBadges sources={concept.sources} />
          {concept.hidden && <span className="rounded bg-slate-200 px-2 py-0.5 text-xs">hidden</span>}
          {!concept.active && <span className="rounded bg-amber-100 px-2 py-0.5 text-xs text-amber-800">not in the latest build</span>}
        </div>
        <div className="mt-1 text-sm text-slate-500">
          {concept.artworkCount.toLocaleString()} artworks · {concept.degree} linked concepts · search weight{' '}
          <WeightLabel weight={concept.weight} curated={concept.curatorWeight != null} />
          {concept.displayName && <> · build name “{concept.buildName}”</>}
          {concept.conceptTypes.length > 0 && <> · {concept.conceptTypes.join(', ')}</>}
          <span className="ml-2 font-mono text-xs text-slate-400">{concept.ucid}</span>
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        <div className="space-y-6 lg:col-span-2">
          <ConceptProposals proposals={data.proposals ?? []} />
          <Card>
            <CardHeader className="flex flex-row items-center justify-between gap-2 pb-3">
              <div>
                <CardTitle className="text-sm">Neighbourhood</CardTitle>
                <CardDescription>
                  Nearest concepts by meaning; colour is category, size is how many artworks carry it.
                  Click a concept to walk the graph; hover to see its links.
                </CardDescription>
              </div>
              <div className="flex shrink-0 gap-2">
                <Button size="sm" variant="outline" onClick={() => setBreadth(breadth === 30 ? 60 : 30)}>
                  {breadth === 30 ? 'Wider' : 'Narrower'}
                </Button>
                <Button size="sm" variant={showArtworks ? 'default' : 'outline'} onClick={() => setShowArtworks(!showArtworks)}>
                  Artworks
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {graph ? (
                <div className={graphLoading ? 'opacity-70' : undefined}>
                  <ConceptGraph graph={graph} />
                </div>
              ) : (
                <Skeleton className="h-[520px] w-full" />
              )}
            </CardContent>
          </Card>

          <Tabs defaultValue="artworks">
            <TabsList>
              <TabsTrigger value="artworks">Artworks ({concept.artworkCount.toLocaleString()})</TabsTrigger>
              <TabsTrigger value="related">Related concepts ({neighbors.length})</TabsTrigger>
              <TabsTrigger value="cooccurring">Appears with ({coOccurring.length})</TabsTrigger>
            </TabsList>
            <TabsContent value="artworks" className="pt-3">
              <ArtworksTab ucid={concept.ucid} total={concept.artworkCount} />
            </TabsContent>
            <TabsContent value="related" className="pt-3">
              <p className="mb-3 text-xs text-slate-500">Closest in meaning (embedding similarity), strongest first.</p>
              <div className="flex flex-wrap gap-2">
                {neighbors.map((n) => (
                  <ConceptChip key={n.ucid} concept={n} extra={n.similarity != null ? n.similarity.toFixed(2) : undefined} />
                ))}
              </div>
            </TabsContent>
            <TabsContent value="cooccurring" className="pt-3">
              <p className="mb-3 text-xs text-slate-500">Concepts found on the same artworks, by number of artworks shared.</p>
              <div className="flex flex-wrap gap-2">
                {coOccurring.map((n) => (
                  <ConceptChip key={n.ucid} concept={n} extra={`${n.shared}`} />
                ))}
              </div>
            </TabsContent>
          </Tabs>
        </div>

        <div className="space-y-6">
          <CuratePanel concept={concept} />
          <ConceptSettings concept={concept} />
          <ConceptStructure concept={concept} rules={data.rules ?? []} />
          <ConceptRelations concept={concept} relations={data.relations ?? []} />

          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm">Names merged into this concept ({concept.memberNames.length})</CardTitle>
              <CardDescription>Every surface form the build resolved to this one concept.</CardDescription>
            </CardHeader>
            <CardContent>
              {aliases.length === 0 ? (
                <p className="text-sm text-slate-500">Only its own name.</p>
              ) : (
                <ul className="space-y-1 text-sm">
                  {aliases.map((m) => (
                    <li key={m} className="text-slate-700">{m}</li>
                  ))}
                </ul>
              )}
              {concept.legacyFacets.length > 0 && (
                <p className="mt-3 text-xs text-slate-500">2024 facets: {concept.legacyFacets.join(', ')}</p>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm">History</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2 text-sm">
              <div className="text-xs text-slate-500">
                First seen {concept.firstSeenRun.replace('run_', '')} · latest {concept.lastSeenRun.replace('run_', '')}
              </div>
              {decisions.length === 0 && <p className="text-slate-500">No curator changes.</p>}
              {decisions.map((d) => (
                <div key={d.id} className="border-l-2 pl-2">
                  <div className="font-medium">{DECISION_LABEL[d.action]}</div>
                  <div className="text-xs text-slate-500">
                    {d.decidedByEmail ?? 'unknown'} · {formatDistanceToNow(new Date(d.decidedAt), { addSuffix: true })}
                  </div>
                  {decisionDetail(d, concept.buildName) && (
                    <div className="text-xs text-slate-600">{decisionDetail(d, concept.buildName)}</div>
                  )}
                </div>
              ))}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  )
}
